/**
 * Domain constants — ported verbatim from krit_deal_engine_PROTOTYPE_v9.html.
 * These anchor the deterministic engines and MUST match the prototype.
 */

/** Deal lifecycle stages. `Intake` is the WhatsApp inbox; everything else is the pipeline. */
export const STATUSES = [
  'Open',
  'Under Review',
  'Interested',
  'In Discussion',
  'Diligence',
  'IC Review',
  'Term Sheet',
  'Invested',
  'Not Interested',
  'Intake',
] as const;
export type Status = (typeof STATUSES)[number];

/**
 * Curated Krit POC (point-of-contact) options for the deal POC dropdown. The UI merges these with
 * any POC names already present on deals, so the list is never empty. Add/remove names here.
 */
export const POCS: string[] = [
  'Aditya Birla',
  'Gaurav Hatalkar',
  'Shubham Gandhi',
  'Sonakshi Pratap',
];

/** Krit Score modules with default weights (weights are admin-editable at runtime). */
export const MODULES = [
  { k: 'qoe', n: 'Financial Integrity & QoE', w: 20 },
  { k: 'ipo', n: 'IPO Readiness & Exit', w: 25 },
  { k: 'val', n: 'Valuation Discipline', w: 15 },
  { k: 'legal', n: 'Legal & Compliance', w: 15 },
  { k: 'promoter', n: 'Promoter & Mgmt', w: 10 },
  { k: 'market', n: 'Market Position & Moat', w: 10 },
  { k: 'capital', n: 'Capital Efficiency', w: 5 },
] as const;
export type ModuleKey = (typeof MODULES)[number]['k'];

/** Default module weights keyed by module. */
export const DEFAULT_WEIGHTS: Record<ModuleKey, number> = Object.fromEntries(
  MODULES.map((m) => [m.k, m.w]),
) as Record<ModuleKey, number>;

/**
 * Every module's untouched placeholder value (a flat, uninformative midpoint) — what a brand-new
 * deal's `ratings` row is created with, before anyone (human or AI) has actually assessed it.
 * NOT a real rating: `isDefaultRatings` below is what tells the UI "this deal has never been
 * rated" so it doesn't present a Krit Score computed from this placeholder as if it meant anything.
 */
export const DEFAULT_RATING_VALUE = 75;
export const DEFAULT_RATINGS: Record<ModuleKey, number> = Object.fromEntries(
  MODULES.map((m) => [m.k, DEFAULT_RATING_VALUE]),
) as Record<ModuleKey, number>;

/** True if every module rating is still sitting at the untouched default — i.e. never rated. */
export function isDefaultRatings(ratings: Record<string, number> | null | undefined): boolean {
  if (!ratings) return true;
  return MODULES.every((m) => (ratings[m.k] ?? DEFAULT_RATING_VALUE) === DEFAULT_RATING_VALUE);
}

/** Per-sector median P/E anchors used when a deal has no explicit peer P/E. */
export const SECTORS: Record<string, { pe: number }> = {
  'Auto Components': { pe: 26 },
  'Specialty Chemicals': { pe: 38 },
  'Pharma CDMO': { pe: 34 },
  'Defence / Aerospace': { pe: 55 },
  'EMS / Electronics': { pe: 60 },
  'Consumer / Food': { pe: 40 },
  'Building Materials': { pe: 28 },
  'Power Equipment': { pe: 45 },
  'Agrochemicals': { pe: 20 },
  'Biofuel / CleanTech': { pe: 35 },
  'Logistics': { pe: 30 },
  'Renewables': { pe: 42 },
};

/** Thesis-fit verdicts. */
export const THESIS_VERDICTS = ['Fit', 'Borderline', 'Pass'] as const;
export type ThesisVerdict = (typeof THESIS_VERDICTS)[number];

/** Diligence item statuses. */
export const DILIGENCE_STATUSES = ['flag', 'open', 'cleared'] as const;
export type DiligenceStatus = (typeof DILIGENCE_STATUSES)[number];

/** Portfolio news feed categories. */
export const NEWS_CATEGORIES = ['Company', 'Sector', 'IPO', 'Market', 'Regulatory', 'Internal'] as const;
export type NewsCategory = (typeof NEWS_CATEGORIES)[number];

/** RBAC roles. */
export const ROLES = ['admin', 'partner', 'analyst', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

/** Deal task workflow — status columns, kind (a plain to-do vs a review), and priority. */
export const TASK_STATUSES = ['todo', 'doing', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_TYPES = ['todo', 'review'] as const;
export type TaskType = (typeof TASK_TYPES)[number];
export const TASK_PRIORITIES = ['low', 'med', 'high'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** Default IC pass threshold (Krit Score). Admin-editable at runtime. */
export const DEFAULT_IC_THRESHOLD = 70;
